<template>
  <section class="task-chat" v-if="showChat || showNotes" data-qid="task-rail-chat">
    <!-- With internal notes the header is the switch between the chat and the notes -->
    <header v-if="showChat && showNotes" class="task-chat__head task-chat__head--tabs">
      <div class="task-chat__tabs">
        <span :class="{'on': tab === 'chat'}" @click="tab = 'chat'" data-qid="task-chat-tab">
          <font-awesome-icon icon="comment" class="mr-1"/>{{ $t('tasks.v2.client_chat') }}
          <span class="task-chat__count" v-if="previousComments.length">{{ previousComments.length }}</span>
        </span>
        <span :class="{'on': tab === 'notes'}" @click="tab = 'notes'" data-qid="task-notes-tab">
          <font-awesome-icon icon="lock" class="mr-1"/>{{ $t('tasks.v2.internal_notes') }}
        </span>
      </div>
    </header>
    <header v-else class="task-chat__head">
      <div class="task-chat__title">
        <h4>{{ showChat ? (client ? $t('tasks.v2.chat_with_auditor') : $t('tasks.v2.chat_with_client')) : $t('tasks.v2.internal_notes') }}</h4>
        <span class="task-chat__sub" v-if="showChat">{{ $tc('tasks.v2.messages_count', previousComments.length, { count: previousComments.length }) }}</span>
      </div>
    </header>

    <template v-if="showChat && (tab === 'chat' || !showNotes)">
      <div class="task-chat__messages" ref="chatContainer" @scroll="onScroll">
        <div v-if="!loadingComments && !previousComments.length" class="task-chat__empty">
          <span class="task-chat__empty-icon"><font-awesome-icon icon="comment"/></span>
          <b>{{ $t('tasks.v2.no_messages') }}</b>
          <span>{{ client ? $t('tasks.v2.chat_empty_client') : $t('tasks.v2.chat_empty') }}</span>
        </div>
        <template v-for="(comment, index) in previousComments">
          <div v-if="newDay(index)" :key="'day-' + comment.id" class="task-chat__day">
            <span>{{ dayLabel(stamp(comment)) }}</span>
          </div>
          <div :key="'comment-' + comment.id"
               class="task-chat__row"
               :class="{'task-chat__row--self': isCommentOwner(comment), 'task-chat__row--cont': !firstOfGroup(index), 'bounce': comment.isNew}"
          >
            <span class="task-chat__avatar">
              <Avatar v-if="!isCommentOwner(comment) && firstOfGroup(index)" :object="comment.user" size="xs"></Avatar>
            </span>
            <div class="task-chat__body">
              <div class="task-chat__name" v-if="!isCommentOwner(comment) && firstOfGroup(index)">
                {{ comment.user ? comment.user.name : '' }}
              </div>
              <div class="task-chat__bubble">
                <div class="text-formatted">{{ comment.comment }}</div>
                <span v-if="isCommentOwner(comment) && !readOnly"
                      class="task-chat__delete"
                      @click="openDeleteCommentModal(comment)"
                      v-b-tooltip.hover
                      :title="$t('system.delete')"
                ><font-awesome-icon icon="trash-alt"/></span>
              </div>
              <div class="task-chat__time">{{ time(stamp(comment)) }}</div>
            </div>
          </div>
        </template>
      </div>

      <!-- New messages while reading further up: the chat stays where it is -->
      <span v-if="newBelow"
            class="task-chat__new"
            @click="scrollToBottom()"
            data-qid="task-chat-new-messages"
      >
        <font-awesome-icon icon="chevron-down" class="mr-1"/>{{ $t('tasks.v2.new_messages_below') }}
      </span>
      <template v-if="canWrite">
        <div class="task-chat__composer">
          <b-form-textarea v-model="comment"
                           rows="1"
                           max-rows="5"
                           no-resize
                           :placeholder="client ? $t('tasks.v2.write_message_auditor') : $t('tasks.v2.write_message')"
                           @keydown.enter.exact.prevent="send"
                           data-qid="chat-input-field"
          ></b-form-textarea>
          <span class="task-chat__send"
                :class="{'task-chat__send--on': hasText}"
                @click="send"
                v-b-tooltip.hover
                :title="$t('tasks.modal.send_button_label')"
                data-qid="chat-send"
          ><font-awesome-icon icon="paper-plane"/></span>
        </div>
        <div class="task-chat__hint">{{ $t('tasks.v2.enter_to_send') }}</div>
      </template>
      <div v-else class="task-chat__closed"><font-awesome-icon icon="lock" class="mr-1"/>{{ closedText }}</div>
    </template>

    <div v-if="showNotes && (tab === 'notes' || !showChat)" class="task-chat__notes">
      <!-- General notes and testing notes, on every stage of the task -->
      <EngagementTaskTextCard v-if="enabled('add_notes')"
                              field="notes"
                              :title="$t('tasks.modal.notes_label')"
                              :required="selectedTask.add_notes === 'Mandatory'"
                              :placeholder="$t('tasks.modal.notes_placeholder')"
      />
      <EngagementTaskTextCard v-if="enabled('add_testing_notes')"
                              field="testing_notes"
                              :title="$t('tasks.modal.testing_notes_label')"
                              :required="selectedTask.add_testing_notes === 'Mandatory'"
                              :placeholder="$t('tasks.modal.testing_notes_placeholder')"
      />
      <div class="task-chat__hint">{{ $t('tasks.v2.notes_hint') }}</div>
    </div>
  </section>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import Avatar from '@/components/Avatar.vue';
import EngagementTaskTextCard from './EngagementTaskTextCard.vue';

// Chat with the client and the auditors' internal notes, always next to the stage pages
export default {
  name: 'EngagementTaskChat',
  mixins: [assessmentSingleTaskProxy],
  components: {
    Avatar,
    EngagementTaskTextCard
  },
  props: {
    // Documentation collection view: chat with the auditors, no internal notes
    client: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tab: 'chat',
      pollingInterval: null,
      newBelow: false,
      lastKey: '',
      // Follows new messages while the reader is at the latest message
      pinned: true,
      resizeObserver: null
    };
  },
  computed: {
    // Clients only get their own side of the task (no data_collection_on), which always has the chat
    showChat() {
      return this.client || !!this.selectedTask.data_collection_on;
    },
    showNotes() {
      return !this.client && (this.enabled('add_notes') || this.enabled('add_testing_notes'));
    },
    closedText() {
      if (this.projectLocked) {
        return this.$t('tasks.v2.chat_closed_project_locked');
      }
      return this.selectedTask.status === 'completed' ? this.$t('tasks.v2.chat_closed_finalized') : this.$t('tasks.v2.chat_closed_delivered');
    },
    canWrite() {
      return this.selectedTask.data_status !== 'completed' && !this.readOnly;
    },
    hasText() {
      return !!(this.comment || '').trim();
    },
    // Changes when a message is added or removed, not when the same messages are loaded again
    commentsKey() {
      const comments = this.previousComments || [];
      const last = comments[comments.length - 1];
      return comments.length + '-' + (last ? last.id : '');
    }
  },
  methods: {
    enabled(setting) {
      return this.selectedTask[setting] === 'Optional' || this.selectedTask[setting] === 'Mandatory';
    },
    send() {
      if (!this.hasText) {
        return;
      }
      this.addCommentSubmit();
    },
    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      this.pinned = true;
      this.newBelow = false;
    },
    nearBottom() {
      const container = this.$refs.chatContainer;
      return !container || container.scrollHeight - container.scrollTop - container.clientHeight < 60;
    },
    onScroll() {
      this.pinned = this.nearBottom();
      if (this.newBelow && this.pinned) {
        this.newBelow = false;
      }
    },
    // The chat opens at the latest message, also when it only gets its size later
    // (the panel opening, the chat sheet on small screens, the notes tab switching back)
    observe() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.$nextTick(() => {
        const container = this.$refs.chatContainer;
        if (!container || !window.ResizeObserver) {
          return;
        }
        this.resizeObserver = new ResizeObserver(() => {
          if (this.pinned) {
            container.scrollTop = container.scrollHeight;
          }
        });
        this.resizeObserver.observe(container);
        _.forEach(container.children, child => this.resizeObserver.observe(child));
        if (this.pinned) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    // The chat only follows new messages when the reader is at the bottom or wrote the message;
    // reloading the same messages (every 20 seconds) never moves it
    commentsChanged() {
      const key = this.commentsKey;
      if (key === this.lastKey) {
        return;
      }
      const first = !this.lastKey || this.lastKey.indexOf('0-') === 0;
      this.lastKey = key;
      const comments = this.previousComments || [];
      const last = comments[comments.length - 1];
      if (first || this.pinned || (last && this.isCommentOwner(last))) {
        this.pinned = true;
        this.observe();
      } else {
        this.newBelow = true;
      }
    },
    stamp(comment) {
      return comment.created_at || comment.updated_at;
    },
    newDay(index) {
      const comments = this.previousComments;
      return index === 0 || String(this.stamp(comments[index])).substring(0, 10) !== String(this.stamp(comments[index - 1])).substring(0, 10);
    },
    // A new sender or a new day starts a group with the sender's name and avatar
    firstOfGroup(index) {
      return this.newDay(index) || this.shouldShowAvatar(this.previousComments[index], index);
    },
    dayLabel(value) {
      const moment = require('moment');
      const days = moment().startOf('day').diff(moment(value).startOf('day'), 'days');
      if (days === 0) {
        return this.$t('projects.schedule.today');
      }
      if (days === 1) {
        return this.$t('tasks.v2.list.yesterday');
      }
      return this.$options.filters.formatDate(value, 'DD MMM YYYY', true);
    },
    time(value) {
      const moment = require('moment');
      return value ? moment(value).format('HH:mm') : '';
    }
  },
  mounted() {
    this.$watch(() => this.commentsKey, () => this.commentsChanged());
    this.$watch(() => this.tab, () => this.observe());
    this.commentsChanged();
    this.observe();
    // New messages show up while the task is open
    this.pollingInterval = setInterval(() => this.loadComments(), 20000);
  },
  beforeDestroy() {
    clearInterval(this.pollingInterval);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
};
</script>
